import { useMemo } from "react";
import { Box, HStack, Icon } from "@chakra-ui/react";
import CDS from "design-system";
import { FiCheck, FiX, FiAlertTriangle, FiMinus, FiStar } from "react-icons/fi";
import moment from "moment-timezone";
import { TimeSlotWithContext, ParticipantStatus, ParticipantSlotStatus } from "./helpers";

interface TimeSlotRowProps {
  slot: TimeSlotWithContext;
  timezone: string;
  hasMultipleTimezones: boolean;
  isSelected: boolean;
  onSelect: (time: Date) => void;
}

type TimezoneGroup = {
  localTime: string;
  status: ParticipantSlotStatus;
  names: string[];
  detail?: string;
};

function getStatusIcon(status: ParticipantSlotStatus) {
  switch (status) {
    case "available":
      return { icon: FiCheck, color: "green.500", textColor: "green.600" };
    case "busy":
      return { icon: FiX, color: "red.500", textColor: "red.600" };
    case "outside_hours":
      return { icon: FiAlertTriangle, color: "orange.500", textColor: "orange.600" };
    case "unknown":
      return { icon: FiMinus, color: "gray.400", textColor: "gray.500" };
  }
}

function groupByTimezone(
  statuses: ParticipantStatus[],
  slotTime: Date,
  displayTimezone: string,
  hasMultipleTimezones: boolean,
): TimezoneGroup[] {
  // Group by (localTime + status) so participants with the same timezone and status appear together
  const groups = new Map<string, TimezoneGroup>();

  for (const ps of statuses) {
    const tz = ps.participant.timezone;
    // Show local time when multiple timezones are present (show all), or when this tz differs from display tz
    const showLocalTime = tz != null && (hasMultipleTimezones || tz !== displayTimezone);
    const localTime = showLocalTime ? moment.tz(slotTime, tz).format("h:mm A z") : "";
    const key = `${localTime}::${ps.status}`;

    // For outside_hours, the detail duplicates the time info — omit it
    const detail = (ps.status !== "outside_hours" && ps.detail && ps.detail !== "Busy") ? ps.detail : undefined;

    const existing = groups.get(key);
    if (existing) {
      existing.names.push(ps.participant.name);
      if (!existing.detail && detail) {
        existing.detail = detail;
      }
    } else {
      groups.set(key, {
        localTime,
        status: ps.status,
        names: [ps.participant.name],
        detail,
      });
    }
  }

  return Array.from(groups.values());
}

function GroupIndicator({ group }: { group: TimezoneGroup }) {
  const { icon, color, textColor } = getStatusIcon(group.status);
  const nameList = group.names.join(", ");
  const localSuffix = group.localTime ? ` · ${group.localTime}` : "";
  const detailSuffix = group.detail ? ` (${group.detail})` : "";

  return (
    <HStack spacing={1}>
      <Icon as={icon} boxSize={3} color={color} />
      <CDS.Text10 color={textColor}>
        {nameList}{localSuffix}{detailSuffix}
      </CDS.Text10>
    </HStack>
  );
}

export default function TimeSlotRow({ slot, timezone, hasMultipleTimezones, isSelected, onSelect }: TimeSlotRowProps) {
  const timeLabel = moment.tz(slot.time, timezone).format("h:mm A");

  const groups = useMemo(
    () => groupByTimezone(slot.participantStatuses, slot.time, timezone, hasMultipleTimezones),
    [slot.participantStatuses, slot.time, timezone, hasMultipleTimezones],
  );

  return (
    <Box
      px={2}
      py={1}
      cursor="pointer"
      borderWidth="1px"
      borderColor={isSelected ? "blue.400" : slot.isSuggested ? "green.200" : "gray.100"}
      borderRadius="md"
      bg={isSelected ? "blue.50" : slot.isSuggested ? "green.50" : "white"}
      _hover={{ bg: isSelected ? "blue.50" : "gray.50" }}
      onClick={() => onSelect(slot.time)}
      transition="background 0.1s"
    >
      <HStack spacing={2} flexWrap="wrap" align="center">
        <CDS.Text10 fontWeight="semibold" minW="60px">
          {timeLabel}
        </CDS.Text10>
        {slot.isSuggested && (
          <HStack spacing={1}>
            <Icon as={FiStar} boxSize={3} color="green.500" />
            <CDS.Text10 color="green.600" fontWeight="medium">
              Suggested
            </CDS.Text10>
          </HStack>
        )}
        {groups.map((group) => (
          <GroupIndicator key={`${group.localTime}-${group.status}`} group={group} />
        ))}
      </HStack>
    </Box>
  );
}
